import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { LifeBuoy, Send, CheckCircle2, Loader2, ChevronRight, Download, Monitor } from "lucide-react";

const inputCls =
  "w-full h-12 px-4 rounded-lg border border-slate-200 bg-white outline-none focus:border-[#1A73E8] focus:ring-4 focus:ring-blue-100 transition-all";
const labelCls = "block text-sm font-medium text-slate-700 mb-1.5";

export default function ContactSupportPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", topic: "", message: "" });
  const [topics, setTopics] = useState([]);
  const [settings, setSettings] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/topics").then((r) => setTopics(r.data)).catch(() => {});
    api.get("/support-settings").then((r) => setSettings(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.message.trim())
      return toast.error("Please fill in name, email, subject and message");
    setSending(true);
    try {
      await api.post("/support", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="support-page" className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <nav className="flex items-center gap-1.5 text-sm text-slate-500 mb-8">
        <Link to="/" className="hover:text-[#1A73E8]">Help Center</Link>
        <ChevronRight className="w-4 h-4 text-slate-300" />
        <span className="text-slate-800 font-medium">Contact support</span>
      </nav>

      <div className="flex items-center gap-3 mb-2">
        <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#E8F0FE] text-[#1A73E8]">
          <LifeBuoy className="w-6 h-6" />
        </span>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{settings?.heading || "Get support"}</h1>
      </div>
      <p className="text-slate-600 mb-8">
        {settings?.intro || "Can't find the answer in our articles? Send us the details and our team will get back to you by email."}
      </p>

      {done ? (
        <div data-testid="support-success" className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
          <CheckCircle2 className="w-12 h-12 text-[#188038] mx-auto" />
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-4">Request received</h2>
          <p className="text-slate-500 mt-2">Thanks, {form.name.split(" ")[0] || "there"}. We've logged your request and will be in touch at {form.email}.</p>
          <Link to="/" className="inline-block mt-6 text-[#1A73E8] font-medium hover:underline">Back to Help Center</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Your name</label>
              <input data-testid="support-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input data-testid="support-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Subject</label>
              <input data-testid="support-subject-input" className={inputCls} value={form.subject} onChange={set("subject")} placeholder="Brief summary" />
            </div>
            <div>
              <label className={labelCls}>Related product <span className="text-slate-400 font-normal">(optional)</span></label>
              <select data-testid="support-topic-select" className={inputCls} value={form.topic} onChange={set("topic")}>
                <option value="">Not sure / general</option>
                {topics.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>How can we help?</label>
            <textarea data-testid="support-message-input" className={inputCls + " h-auto py-3"} rows={6} value={form.message} onChange={set("message")} placeholder="Describe what you need help with, including any error messages." />
          </div>
          <button data-testid="support-submit-btn" type="submit" disabled={sending} className="w-full h-12 rounded-lg bg-[#1A73E8] hover:bg-[#174EA6] text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Send request
          </button>
        </form>
      )}

      <p className="text-center text-sm text-slate-500 mt-8">
        Want to cancel your plan or account? <Link to="/cancel" className="text-[#1A73E8] font-medium hover:underline">Submit a cancellation request</Link>
      </p>

      {/* Remote support download */}
      {(settings?.show_remote_tool ?? true) && (
      <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 sm:p-7 flex flex-col sm:flex-row items-center gap-5 text-center sm:text-left">
        <span className="grid place-items-center w-12 h-12 rounded-xl bg-[#E8F0FE] text-[#1A73E8] shrink-0">
          <Monitor className="w-6 h-6" />
        </span>
        <div className="flex-1">
          <h3 className="font-heading text-lg font-semibold text-slate-900">{settings?.remote_title || "Support tool"}</h3>
          <p className="text-sm text-slate-500 mt-1">
            {settings?.remote_description || "."}
          </p>
        </div>
        <a
          href={`${process.env.PUBLIC_URL || ""}/downloads/HopToDesk.zip`}
          download
          data-testid="download-remote-tool-btn"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-[#1A73E8] hover:bg-[#174EA6] text-white font-semibold transition-colors shrink-0"
        >
          <Download className="w-5 h-5" /> {settings?.remote_button_label || "Support"}
        </a>
      </div>
      )}
    </main>
  );
}
